import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import SEO from '../components/SEO'
import { breadcrumbSchema } from '../components/SEO'

export default function BlogPost() {
  const { slug } = useParams()
  const [post, setPost] = useState(null)
  const [comments, setComments] = useState([])
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState({ name: '', email: '', content: '' })
  const [submitting, setSubmitting] = useState(false)
  const [msg, setMsg] = useState('')

  useEffect(() => {
    setLoading(true)
    fetch(`/api/blog/posts/slug/${slug}`)
      .then(r => r.json())
      .then(data => {
        if (data.error) { setPost(null) } else { setPost(data); setComments(data.comments || []) }
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [slug])

  const submitComment = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    setMsg('')
    try {
      const res = await fetch('/api/blog/comments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, post_id: post.id })
      })
      const data = await res.json()
      if (res.ok) {
        setMsg('Comment submitted!')
        setForm({ name: '', email: '', content: '' })
      } else {
        setMsg(data.error || 'Failed to submit comment')
      }
    } catch {
      setMsg('Network error')
    } finally {
      setSubmitting(false)
    }
  }

  const related = []
  if (post) {
    // placeholder; backend could add relatedPosts query later
  }

  const breadcrumbItems = post ? [
    { name: 'Home', url: '/' },
    { name: 'Blog', url: '/blog' },
    { name: post.title, url: null },
  ] : undefined

  if (loading) {
    return (
      <>
        <SEO title="Blog Post" description="Loading article..." canonical="/blog" noIndex structuredData={{ '@context': 'https://schema.org', '@type': 'WebPage', name: 'Loading' }} />
        <Navbar />
        <div className="blog-page"><div className="container" style={{ padding: '60px 0' }}><div className="spinner"></div></div></div>
        <Footer />
      </>
    )
  }

  if (!post) {
    return (
      <>
        <SEO title="Post Not Found" description="The article you're looking for does not exist or is no longer published." canonical="/blog" noIndex structuredData={{ '@context': 'https://schema.org', '@type': 'WebPage', name: 'Post Not Found', description: 'The article you are looking for does not exist.' }} />
        <Navbar />
        <div className="blog-page"><div className="container" style={{ padding: '80px 0', textAlign: 'center' }}>
          <h2>Post not found</h2>
          <p style={{ color: 'var(--text-muted)' }}>The article you're looking for does not exist or is no longer published.</p>
          <Link to="/blog" className="btn btn-primary" style={{ marginTop: '20px' }}>Back to Blog</Link>
        </div></div>
        <Footer />
      </>
    )
  }

  return (
    <>
      <SEO
        title={post.meta_title || post.title}
        description={post.meta_description || post.excerpt}
        canonical={`/blog/${post.slug}`}
        structuredData={[
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: post.title,
            description: post.meta_description || post.excerpt,
            image: post.cover_image ? `https://www.tredextechnology.in${post.cover_image}` : undefined,
            datePublished: post.published_at || post.created_at,
            dateModified: post.updated_at || post.published_at || post.created_at,
            author: post.author_name ? { '@type': 'Person', name: post.author_name } : undefined,
            publisher: {
              '@type': 'Organization',
              name: 'TRED-EX AI',
              logo: { '@type': 'ImageObject', url: 'https://www.tredextechnology.in/icons.svg' }
            },
            mainEntityOfPage: `https://www.tredextechnology.in/blog/${post.slug}`
          },
          breadcrumbSchema(breadcrumbItems)
        ]}
      />
      <Navbar />
      <div className="blog-page">
        <div className="container">
          <div className="blog-post">
            {post.cover_image && (
              <div className="blog-post-hero">
                <img src={post.cover_image} alt={post.title} />
              </div>
            )}
            <div className="blog-post-content">
              <div className="blog-post-meta">
                {post.category_name && <span className="category-badge">{post.category_name}</span>}
                <span><i className="fas fa-calendar"></i> {new Date(post.published_at || post.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                <span><i className="fas fa-clock"></i> {post.reading_time || 5} min read</span>
                <span><i className="fas fa-eye"></i> {post.views || 0} views</span>
                {post.author_name && <span><i className="fas fa-user"></i> {post.author_name}</span>}
              </div>
              <h1>{post.title}</h1>
              <div className="blog-post-body prose" dangerouslySetInnerHTML={{ __html: post.content || '' }} />
            </div>

            <div className="blog-post-footer">
              {post.tags?.length > 0 && (
                <div className="blog-tags">
                  {post.tags.map(tag => (
                    <Link key={tag.id} to={`/blog/tag/${tag.slug}`} className="tag-chip">#{tag.slug}</Link>
                  ))}
                </div>
              )}
            </div>

            <div className="blog-comments">
              <h3><i className="fas fa-comments"></i> Comments ({comments.length})</h3>
              {comments.length === 0 && <p className="blog-comments-empty">No comments yet. Be the first to share your thoughts!</p>}
              <div className="comment-list">
                {comments.map(c => (
                  <div key={c.id} className="comment-item">
                    <div className="comment-header">
                      <strong>{c.name}</strong>
                      <span>{new Date(c.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                    </div>
                    <p>{c.content}</p>
                  </div>
                ))}
              </div>
              <form className="comment-form" onSubmit={submitComment}>
                <h4>Leave a comment</h4>
                {msg && <div className="form-status">{msg}</div>}
                <div className="form-row">
                  <input required placeholder="Your name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                  <input required type="email" placeholder="Your email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                </div>
                <textarea required placeholder="Write your comment..." value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} rows={4} />
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Comment'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  )
}
